// frontend/src/super-admin/AgentReleasesPage.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { PageHeader } from '../design-system/components/PageHeader';
import { LoadingState } from '../design-system/components/LoadingState';
import { ErrorState } from '../design-system/components/ErrorState';
import { VISUAL } from '../design-system/visual';
import { AlertTriangle, Download } from 'lucide-react';
import { useAdminPermission } from '../core/auth/useAdminPermission';
import { agentReleasesApi, AgentRelease } from '../modules/agent-releases/api';

export function AgentReleasesPage() {
  const canWrite = useAdminPermission('releases.write');
  const [release, setRelease] = React.useState<AgentRelease | null>(null);
  const [loadErr, setLoadErr] = React.useState<string | null>(null);
  const [version, setVersion] = React.useState('');
  const [macUrl, setMacUrl] = React.useState('');
  const [winUrl, setWinUrl] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoadErr(null);
    setRelease(null);
    try {
      const r = await agentReleasesApi.get();
      setRelease(r);
      setVersion(r.version ?? '');
      setMacUrl(r.macos_url ?? '');
      setWinUrl(r.windows_url ?? '');
      setNotes(r.notes ?? '');
    } catch (err: any) {
      setLoadErr(String(err?.message ?? err));
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return; // Enter-key submit bypasses the disabled button
    setBusy(true);
    try {
      const r = await agentReleasesApi.update({
        version: version.trim() || null,
        macos_url: macUrl.trim() || null,
        windows_url: winUrl.trim() || null,
        notes: notes.trim() || null,
      });
      setRelease(r);
      toast.success('Saved');
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally { setBusy(false); }
  }

  if (loadErr) return <ErrorState description={loadErr} onRetry={load} />;
  if (!release) return <LoadingState variant="page" />;

  return (
    <div>
      <PageHeader
        eyebrow="Platform / Releases"
        title="Desktop agent installers"
        description="The download URLs tenants see in their monitoring settings. We don't host installers — paste public URLs (Vercel Blob is fine)."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-3xl space-y-6">
        <form onSubmit={save} className="space-y-6">
          <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
            <div className="flex items-center gap-3 mb-5">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-saffron/15 ring-1 ring-saffron/25">
                <Download className="h-4 w-4 text-saffron-foreground" strokeWidth={1.7} />
              </div>
              <h3 className={VISUAL.dashSection}>Current release</h3>
            </div>
            <div className="grid gap-5">
              <div className="grid gap-1.5">
                <Label>Version</Label>
                <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.0.0" />
                <p className="text-[12px] text-muted-foreground">Semver. Stored as text; agent does not auto-update yet.</p>
              </div>
              <div className="grid gap-1.5">
                <Label>macOS installer URL <span className="text-muted-foreground font-normal">(.dmg)</span></Label>
                <Input value={macUrl} onChange={(e) => setMacUrl(e.target.value)} placeholder="https://blob.vercel-storage.com/worqly-agent-1.0.0.dmg" />
              </div>
              <div className="grid gap-1.5">
                <Label>Windows installer URL <span className="text-muted-foreground font-normal">(.exe)</span></Label>
                <Input value={winUrl} onChange={(e) => setWinUrl(e.target.value)} placeholder="https://blob.vercel-storage.com/worqly-agent-1.0.0.exe" />
              </div>
              <div className="grid gap-1.5">
                <Label>Release notes <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="What's new in this version" />
              </div>
            </div>
          </section>

          <div className="flex items-center justify-between pt-2">
            {release.published_at ? (
              <p className="text-[12.5px] text-muted-foreground">
                Last published <span className="text-foreground font-semibold">{new Date(release.published_at).toLocaleString()}</span>
              </p>
            ) : <span />}
            <div className="flex items-center gap-3">
              {!canWrite && (
                <p className="text-[12px] text-muted-foreground">Read-only — you need the releases.write permission to publish.</p>
              )}
              <Button
                type="submit"
                disabled={busy || !canWrite}
                title={canWrite ? undefined : 'Requires the releases.write permission'}
                className="rounded-full h-11 px-6 text-[14px] font-semibold"
              >
                {busy ? 'Saving…' : 'Save & publish'}
              </Button>
            </div>
          </div>
        </form>

        <div className="rounded-2xl ring-1 ring-warning/30 bg-warning/10 px-5 py-4 flex items-start gap-3">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" strokeWidth={1.8} />
          <p className="text-[12.5px] text-foreground leading-relaxed">
            <strong>Heads up.</strong> Worqly does not host installers. Upload your built <code className="font-mono text-[11.5px]">.dmg</code> / <code className="font-mono text-[11.5px]">.exe</code> to Vercel Blob (or any HTTPS host) yourself, then paste the public URL above.
          </p>
        </div>
      </div>
    </div>
  );
}
